import {IService, SystemComponentType} from '@gongxigames/framework/core/types';
import {injectable} from '@gongxigames/framework/core/installer';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {CoreDI} from '@gongxigames/framework/core/configs/CoreDI';

type ReasonToOverride = {
    regulationType?: string;
    skin: string;
    changeWinToPayout: boolean;
};

@injectable()
export class LanguageService implements IService {
    public static NAME: symbol = CoreDI.LanguageService;
    public type: SystemComponentType = 'service';
    protected static readonly START_TAG: string = '<no/>';
    protected static readonly END_TAG: string = '</no>';

    protected _texts: Record<string, string> = {};

    public get(id: string, textValues?: Record<string, string | number>): string {
        let text = this._texts[id];

        if (!text) {
            consoleLogger.warn(`There's no string ${id}`);

            return 'undefined';
        }

        text = this.formatString(id);

        if (!textValues) {
            return text;
        }

        return this.setTextValues(text, textValues);
    }

    protected formatString(id: string): string {
        let text = this._texts[id];
        const getRegExp = (value: string): RegExp => {
            return new RegExp(value, 'g');
        };
        const warmError = (tag: string): void => {
            consoleLogger.warn(`Localization TAG ${tag} missed!\nPlease, check localization KEY "${id}"!`);
        };
        const startTag = text.match(getRegExp(LanguageService.START_TAG));
        const endTag = text.match(getRegExp(LanguageService.END_TAG));

        if (startTag && endTag) {
            if (startTag.length === endTag.length) {
                text = text.replace(getRegExp(LanguageService.START_TAG), '').replace(getRegExp(LanguageService.END_TAG), '');
            } else if (startTag.length > endTag.length) {
                warmError(LanguageService.END_TAG);
            } else if (startTag.length < endTag.length) {
                warmError(LanguageService.START_TAG);
            }
        }

        return text;
    }

    public setTexts(texts: Record<string, string>): void {
        this._texts = texts;
    }

    public mergeTexts(texts: Record<string, string>): void {
        this._texts = {...this._texts, ...texts};
    }

    public overrideFreezedTexts(): void {
        const keyToSearch = '__FREEZED__';
        const freezedKeys = Object.keys(this.texts).filter(key => key.endsWith(keyToSearch));

        freezedKeys.forEach(key => {
            const [cleanKey] = key.split(keyToSearch);

            if (this.texts[cleanKey] !== undefined) {
                this.texts[cleanKey] = this.texts[key];
            }
        });

        freezedKeys.forEach(key => {
            delete this.texts[key];
        });
    }

    public applyTextsOverride(reasons: ReasonToOverride): void {
        const allReasons = Object.entries(reasons);

        Object.keys(this._texts)
            .filter((originalKey) => {
                return allReasons.filter(([reason, particularValue]) => {
                    return originalKey.includes(`${reason}:${particularValue}`);
                }).length;
            })
            .forEach((originalKey) => {
                let keyToOverride = originalKey;

                allReasons.forEach(([reason, particularValue]) => {
                    keyToOverride = keyToOverride.replace(`${reason}:${particularValue}`, '');
                });
                keyToOverride = keyToOverride.replace(/\[,*]/, '');

                if (this._texts[keyToOverride]) {
                    this._texts[keyToOverride] = this._texts[originalKey];
                }
            });
    }

    public get texts(): Record<string, string> {
        return this._texts;
    }

    public destroy(): void {
        this._texts = {};
    }

    protected setTextValues(text: string, textValues: Record<string, string | number>): string {
        for (const [key, val] of Object.entries(textValues)) {
            const from = `{{${key}}}`;
            const to = String(val);

            while (text.includes(from)) {
                text = text.replace(from, to);
            }
        }

        return text;
    }
}
